/*
 * The pins of a conversation, as the client keeps them (X-A4 N1): who fastened which message, and
 * the message behind each pin so the pinned list draws a card without a second round trip.
 *
 * A pin is a fact about the conversation and never a field of the message, so it lives in a store
 * of its own rather than on the rows of the model: the timeline's marker and the pinned list read
 * one slice per conversation, loaded the first time anybody asks for it and kept live by the
 * `chat.pin.changed` event — heard through the client store's tap, the same way Activity hears
 * its own events, so nothing here waits for a reducer. The two routes, the list and the pin switch,
 * are the conversation's (`api.ts`).
 */

import { useEffect } from "react"
import { createStore, useStore } from "zustand"
import type { Render } from "../../../core/render/nodes.ts"
import type { ConversationId, MessageId } from "../../../core/store/model.ts"
import { client } from "../../../core/store/store.ts"
import type { WireEvent } from "../../../core/wire/index.ts"
import type { PinChanged, PinView } from "../wire/chat.gen.ts"
import type { MessageView } from "../wire/views.ts"
import { fetchMessageBySeq, fetchPinned } from "./api.ts"

export type { PinView }

export interface PinsSlice {
	status: "idle" | "loading" | "ready" | "failed"
	/** Newest pin first, as the server orders them. */
	pins: PinView[]
	/** The live message behind each pin, by message id. */
	messages: Record<MessageId, MessageView>
	/**
	 * Whether the conversation has pins this page does not carry (`protocol.md` §3.2: the route
	 * answers at most `historyPageMax`). It takes no cursor, so there is nothing the list could ask
	 * for — what it can do is say that the oldest pins are not on screen, instead of showing the
	 * newest page as if it were all of them.
	 */
	hasMore: boolean
}

interface PinsState {
	byConv: Record<ConversationId, PinsSlice>
}

const EMPTY: PinsSlice = { status: "idle", pins: [], messages: {}, hasMore: false }

export const pinsStore = createStore<PinsState>(() => ({ byConv: {} }))

function sliceOf(conv: ConversationId): PinsSlice {
	return pinsStore.getState().byConv[conv] ?? EMPTY
}

function setSlice(conv: ConversationId, patch: (slice: PinsSlice) => PinsSlice): void {
	pinsStore.setState((state) => ({
		byConv: { ...state.byConv, [conv]: patch(state.byConv[conv] ?? EMPTY) },
	}))
}

// ---- loading and listening ----

/**
 * Loads a conversation's pins once; a failed load is retried by the next `reloadPins`.
 *
 * A conversation id of 0 asks for nothing. A row that is not in a conversation yet is drawn from a
 * synthetic message — the pending row of a send carries `{id: 0, conv: 0}` (`message/PendingRow.tsx`)
 * — and the guard is here rather than in its caller so that the next surface to invent a row cannot
 * make the most ordinary action in the product fetch a conversation that does not exist.
 */
export function ensurePins(conv: ConversationId): void {
	if (conv <= 0) return
	if (sliceOf(conv).status !== "idle") return
	listen()
	void reloadPins(conv)
}

/** Fetches the list afresh — the first load, a retry after a failure, a pin event with a message the list did not hold. */
export async function reloadPins(conv: ConversationId): Promise<void> {
	setSlice(conv, (slice) => ({ ...slice, status: "loading" }))
	try {
		const page = await fetchPinned(conv)
		const messages: Record<MessageId, MessageView> = {}
		for (const entry of page.pinned) messages[entry.message.id] = entry.message
		setSlice(conv, () => ({
			status: "ready",
			pins: page.pinned.map((entry) => entry.pin),
			messages,
			hasMore: page.hasMore,
		}))
	} catch {
		setSlice(conv, (slice) => ({ ...slice, status: "failed" }))
	}
}

let stopTap: (() => void) | undefined

/**
 * Hears every applied event once, for the life of the session. A pin lands at the front of the
 * list with its message read through the permalink route; an unpin, and a deleted message, leave
 * it; an edit refreshes the card's body. A conversation whose list was never loaded is left alone
 * — it will be read whole when somebody first asks for it.
 */
function listen(): void {
	stopTap ??= client.onEvent((event) => {
		apply(event)
	})
}

/**
 * The three events the list follows. The type is read as a string on purpose: the module's event
 * union (`events.ts`) is the reducer's contract, and this tap must not fail to compile while the
 * union does not yet name `chat.pin.changed` — the event arrives either way, the store answers
 * "unknown" for it, and the tap is what applies it.
 */
function apply(event: WireEvent): void {
	const { type, data } = event as { type: string; data: unknown }
	switch (type) {
		case "chat.pin.changed": {
			const changed = data as PinChanged
			const slice = sliceOf(changed.conv)
			if (slice.status === "idle") return
			if (!changed.pinned) {
				setSlice(changed.conv, (current) => ({
					...current,
					pins: current.pins.filter((pin) => pin.messageId !== changed.messageId),
				}))
				return
			}
			if (slice.pins.some((pin) => pin.messageId === changed.messageId)) return
			const pin: PinView = {
				messageId: changed.messageId,
				conv: changed.conv,
				seq: changed.seq,
				byUserId: changed.byUserId,
				at: changed.at,
			}
			void fetchMessageBySeq(changed.conv, changed.seq)
				.then(({ message }) =>
					setSlice(changed.conv, (current) => ({
						...current,
						pins: [pin, ...current.pins.filter((entry) => entry.messageId !== pin.messageId)],
						messages: { ...current.messages, [message.id]: message as MessageView },
					})),
				)
				.catch(() => reloadPins(changed.conv))
			return
		}
		case "chat.message.deleted": {
			const deleted = data as { conv: number; id: number }
			if (sliceOf(deleted.conv).status === "idle") return
			setSlice(deleted.conv, (current) => ({
				...current,
				pins: current.pins.filter((pin) => pin.messageId !== deleted.id),
			}))
			return
		}
		case "chat.message.edited": {
			const edited = data as { conv: number; id: number; markdown: string; render: Render }
			const message = sliceOf(edited.conv).messages[edited.id]
			if (message === undefined) return
			setSlice(edited.conv, (current) => ({
				...current,
				messages: {
					...current.messages,
					[edited.id]: { ...message, markdown: edited.markdown, render: edited.render },
				},
			}))
			return
		}
		default:
			return
	}
}

// ---- reading ----

/** The conversation's pins, loading them the first time a surface asks. */
export function usePins(conv: ConversationId): PinsSlice {
	const slice = useStore(pinsStore, (state) => state.byConv[conv] ?? EMPTY)
	useEffect(() => {
		ensurePins(conv)
	}, [conv])
	return slice
}

/**
 * One message's pin, or undefined while it is not pinned (or the list is not in yet). A message that
 * is not in a conversation yet — the pending row of a send — has no pin and asks for none.
 */
export function usePinOf(conv: ConversationId, messageId: MessageId): PinView | undefined {
	const pin = useStore(pinsStore, (state) =>
		state.byConv[conv]?.pins.find((entry) => entry.messageId === messageId),
	)
	useEffect(() => {
		ensurePins(conv)
	}, [conv])
	return conv <= 0 || messageId <= 0 ? undefined : pin
}

/** Forgets everything — a test's reset, and what a sign-out sweep would call. */
export function resetPins(): void {
	pinsStore.setState({ byConv: {} }, true)
	stopTap?.()
	stopTap = undefined
}
